import { ArrowUpCircle, Package, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

import { AddonsAddonListItem } from '@api/types.gen';

import { ExternalLink } from '@/react/components/ExternalLink';

import { Card } from '@@/primitives/Card';
import { LinkButton } from '@@/LinkButton';
import { Link } from '@@/Link';
import { FallbackImage } from '@@/FallbackImage';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { Button, LoadingButton } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { AddonStatusBadge } from '../AddonStatusBadge';
import { AddonVersionBadge } from '../AddonVersionBadge';
import { getAddonLifecycleState } from '../addonLifecycleState';
import {
  confirmAddonUninstall,
  useUninstallAddon,
} from '../queries/useUninstallAddon';
import { buildUpgradeParams } from '../upgradeDeepLink';
import {
  useAddonRestart,
  confirmAddonRestart,
} from '../DetailView/useAddonRestart';
import { useAddonNamespaceExists } from '../DetailView/queries/useAddonRelease';
import {
  installBlockedMessage,
  launchBlockedMessage,
} from '../catalog-messages';

interface Props {
  addon: AddonsAddonListItem;
  // disabled gates the addon lifecycle actions when Portainer is not running on
  // Kubernetes, mirroring the platform gate that disables the lifecycle API.
  disabled?: boolean;
  // catalogUnavailable tells the two causes of a missing catalog entry apart;
  // see catalog-messages.
  catalogUnavailable?: boolean;
}

export function AddonCard({
  addon,
  disabled = false,
  catalogUnavailable = false,
}: Props) {
  const {
    isFailed,
    isInstalling,
    isUnknown,
    isUninstalling,
    isInProgress,
    canUninstall,
    notInstalled,
  } = getAddonLifecycleState(addon);
  // An installed add-on keeps its route through its stored catalog details, but
  // never the chart reference — only the live catalog supplies that.
  const canLaunch = !!addon.path;
  const hasLiveCatalogEntry = !!addon.record?.helmChartRepository;
  const showInstall =
    !isInstalling && !isUninstalling && !isUnknown && !addon.enabled;

  const uninstallMutation = useUninstallAddon(addon.id ?? '');
  const restartMutation = useAddonRestart(addon.id ?? '');
  const namespaceExistsQuery = useAddonNamespaceExists(addon.id ?? '');
  // Restart rolls the namespace's deployments. Show at least when the namespace exists and the addon has any status (not uninstalled)
  const showRestart =
    !!namespaceExistsQuery.data &&
    !notInstalled &&
    !isUninstalling &&
    !isFailed;

  // Link to the detail view whenever there's a live release to open
  const isClickable =
    (!!addon.enabled && !isUninstalling) || isInstalling || isUnknown;

  const latestVersion = addon.record?.availableVersions?.[0];

  return (
    <div>
      <Card.Container
        aria-label={addon.displayName}
        className={clsx(
          'h-full',
          isClickable &&
            'relative cursor-pointer transition-colors hover:border-blue-7 hover:bg-gray-2 th-highcontrast:hover:bg-gray-warm-10 th-dark:hover:bg-gray-iron-10'
        )}
      >
        {isClickable && (
          <Link
            to="portainer.addons.addon"
            params={{ id: addon.id }}
            className="absolute inset-0 z-0"
            aria-label={`Open ${addon.displayName} details`}
            data-cy="addon-card-link"
          />
        )}
        <Card.Body className="flex h-full flex-wrap gap-4 overflow-auto">
          <div className="flex flex-1 gap-4">
            <FallbackImage
              fallbackIcon={<Package className="text-4xl" />}
              src={addon.icon}
              alt={addon.displayName}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md object-contain"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-gray-9 th-highcontrast:text-white th-dark:text-white">
                  {addon.displayName}
                </span>
                <AddonVersionBadge record={addon.record} />
                <AddonStatusBadge
                  lifecycleStatus={addon.lifecycleStatus}
                  lifecycleMessage={addon.lifecycleStatusMessage}
                  healthStatus={addon.healthStatus}
                  healthMessage={addon.healthMessage}
                />
              </div>
              <span className="text-sm text-gray-9 th-highcontrast:text-white th-dark:text-white">
                {addon.description}
              </span>
            </div>
          </div>
          <div className="relative z-10 ml-auto mt-auto flex items-end gap-2">
            {addon.enabled && (
              <>
                {addon.record?.upgradeAvailable &&
                  latestVersion &&
                  !isInProgress && (
                    <LinkButton
                      to="portainer.addons.addon"
                      params={buildUpgradeParams(addon.id)}
                      color="primary"
                      size="medium"
                      icon={ArrowUpCircle}
                      title={`Upgrade to v${latestVersion}`}
                      data-cy="addon-card-upgrade-button"
                    >
                      Upgrade
                    </LinkButton>
                  )}
                <LaunchAction
                  path={canLaunch ? addon.path : undefined}
                  catalogUnavailable={catalogUnavailable}
                />
              </>
            )}
            {showRestart && (
              <LoadingButton
                color="secondary"
                size="medium"
                icon={RefreshCw}
                onClick={handleRestart}
                disabled={disabled}
                isLoading={restartMutation.isLoading}
                loadingText="Restarting..."
                data-cy="addon-card-restart-button"
              >
                Restart
              </LoadingButton>
            )}
            {showInstall && (
              // A failed first install has no live release, so it falls here:
              // the install route doubles as the retry.
              <TooltipWithChildren
                message={installBlockedReason()}
                appendTo={document.body}
              >
                <LinkButton
                  to="portainer.addons.install"
                  params={{ id: addon.id }}
                  color="primary"
                  size="medium"
                  disabled={!!installBlockedReason()}
                  data-cy="addon-install-button"
                >
                  {getInstallButtonLabel()}
                </LinkButton>
              </TooltipWithChildren>
            )}
            {canUninstall && !isInstalling && (
              // onClick, not confirmMessage: the confirmation also asks whether
              // to discard the add-on's stored settings.
              <DeleteButton
                onClick={handleUninstall}
                disabled={disabled || isInProgress}
                isLoading={isUninstalling || uninstallMutation.isLoading}
                loadingText="Loading..."
                size="medium"
                data-cy="addon-uninstall-button"
              >
                Uninstall
              </DeleteButton>
            )}
          </div>
        </Card.Body>
      </Card.Container>
    </div>
  );

  // Empty when installing is possible. Both causes disable the same button, so
  // they resolve to one message rather than competing for the tooltip.
  function installBlockedReason() {
    if (disabled) {
      return 'Add-ons require a Kubernetes deployment.';
    }

    if (!hasLiveCatalogEntry) {
      return installBlockedMessage(catalogUnavailable);
    }

    return '';
  }

  function getInstallButtonLabel() {
    if (disabled) return 'Unavailable';
    return isFailed ? 'Retry install' : 'Install';
  }

  async function handleUninstall() {
    const options = await confirmAddonUninstall(addon.displayName ?? 'Add-on');
    if (!options) {
      return;
    }

    uninstallMutation.mutate(options);
  }

  async function handleRestart() {
    if (await confirmAddonRestart(addon.displayName ?? 'Add-on')) {
      restartMutation.mutate();
    }
  }
}

// LaunchAction opens the add-on through the gateway. Without a path there is no
// route to open, so it stays visible but disabled rather than disappearing.
function LaunchAction({
  path,
  catalogUnavailable,
}: {
  path?: string;
  catalogUnavailable: boolean;
}) {
  if (path) {
    return (
      <ExternalLink
        to={path}
        className="btn btn-secondary items-center gap-1"
        data-cy="addon-launch-link"
      >
        Launch
      </ExternalLink>
    );
  }

  return (
    <TooltipWithChildren
      message={launchBlockedMessage(catalogUnavailable)}
      appendTo={document.body}
    >
      <Button
        color="secondary"
        size="medium"
        disabled
        data-cy="addon-launch-link"
      >
        Launch
      </Button>
    </TooltipWithChildren>
  );
}
